import { router } from '@inertiajs/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useState } from 'react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { ChevronDown, ChevronUp, UserCog } from 'lucide-react'

interface User {
  id: number
  name: string
  email: string
  admin: boolean
  created_at: string
  // Clinician profile fields
  credentials: string[]
  specialty?: string
  npi_number?: string
  state_license_numbers: string[]
  license_states: string[]
  board_certifications: string[]
  // Practice & Affiliation
  practice_name?: string
  practice_type?: string
  affiliated_organizations: string[]
  practice_addresses: string[]
  zip_codes_served: string[]
  correctional_facility_access: boolean
  community_health_center_affiliation: boolean
  // Care Cascade Roles
  care_cascade_roles: string[]
}

interface Options {
  credentials: MultiSelectOption[]
  practice_types: MultiSelectOption[]
  care_cascade_roles: MultiSelectOption[]
  us_states: MultiSelectOption[]
}

interface AdminUserEditProps {
  auth: {
    user: User
  }
  user: User
  options: Options
  errors?: {
    name?: string[]
    credentials?: string[]
    practice_type?: string[]
    care_cascade_roles?: string[]
    npi_number?: string[]
  }
}

const userEditSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100, 'Name must not exceed 100 characters'),
  admin: z.boolean().default(false),
  // Clinician profile fields (all optional)
  credentials: z.array(z.string()).default([]),
  specialty: z.string().optional().nullable(),
  npi_number: z.string().optional().nullable(),
  state_license_numbers: z.array(z.string()).default([]),
  license_states: z.array(z.string()).default([]),
  board_certifications: z.array(z.string()).default([]),
  // Practice & Affiliation
  practice_name: z.string().optional().nullable(),
  practice_type: z.string().optional().nullable(),
  affiliated_organizations: z.array(z.string()).default([]),
  practice_addresses: z.array(z.string()).default([]),
  zip_codes_served: z.array(z.string()).default([]),
  correctional_facility_access: z.boolean().default(false),
  community_health_center_affiliation: z.boolean().default(false),
  // Care Cascade Roles
  care_cascade_roles: z.array(z.string()).default([]),
})

type UserEditFormData = z.infer<typeof userEditSchema>

export default function AdminUserEdit({ user, options, errors }: AdminUserEditProps) {
  const [credentialsOpen, setCredentialsOpen] = useState(
    (user.credentials?.length > 0) ||
    !!user.specialty ||
    !!user.npi_number ||
    (user.license_states?.length > 0)
  )
  const [practiceOpen, setPracticeOpen] = useState(
    !!user.practice_name ||
    !!user.practice_type ||
    (user.affiliated_organizations?.length > 0) ||
    user.correctional_facility_access ||
    user.community_health_center_affiliation
  )
  const [rolesOpen, setRolesOpen] = useState(user.care_cascade_roles?.length > 0)

  const { register, handleSubmit, watch, setValue, formState: { errors: formErrors, isSubmitting } } = useForm<UserEditFormData>({
    resolver: zodResolver(userEditSchema),
    defaultValues: {
      name: user.name,
      admin: user.admin,
      credentials: user.credentials || [],
      specialty: user.specialty || '',
      npi_number: user.npi_number || '',
      state_license_numbers: user.state_license_numbers || [],
      license_states: user.license_states || [],
      board_certifications: user.board_certifications || [],
      practice_name: user.practice_name || '',
      practice_type: user.practice_type || '',
      affiliated_organizations: user.affiliated_organizations || [],
      practice_addresses: user.practice_addresses || [],
      zip_codes_served: user.zip_codes_served || [],
      correctional_facility_access: user.correctional_facility_access || false,
      community_health_center_affiliation: user.community_health_center_affiliation || false,
      care_cascade_roles: user.care_cascade_roles || [],
    },
  })

  const adminValue = watch('admin')
  const credentialsValue = watch('credentials')
  const licenseStatesValue = watch('license_states')
  const careCascadeRolesValue = watch('care_cascade_roles')
  const practiceTypeValue = watch('practice_type')
  const correctionalAccessValue = watch('correctional_facility_access')
  const chcAffiliationValue = watch('community_health_center_affiliation')

  // Manage string arrays as comma-separated inputs
  const [licenseNumbersInput, setLicenseNumbersInput] = useState(
    (user.state_license_numbers || []).join(', ')
  )
  const [certificationsInput, setCertificationsInput] = useState(
    (user.board_certifications || []).join(', ')
  )
  const [organizationsInput, setOrganizationsInput] = useState(
    (user.affiliated_organizations || []).join(', ')
  )
  const [addressesInput, setAddressesInput] = useState(
    (user.practice_addresses || []).join('\n')
  )
  const [zipCodesInput, setZipCodesInput] = useState(
    (user.zip_codes_served || []).join(', ')
  )

  const onSubmit = (data: UserEditFormData) => {
    // Convert comma-separated strings to arrays
    const submitData = {
      ...data,
      state_license_numbers: licenseNumbersInput ? licenseNumbersInput.split(',').map(s => s.trim()).filter(Boolean) : [],
      board_certifications: certificationsInput ? certificationsInput.split(',').map(s => s.trim()).filter(Boolean) : [],
      affiliated_organizations: organizationsInput ? organizationsInput.split(',').map(s => s.trim()).filter(Boolean) : [],
      practice_addresses: addressesInput ? addressesInput.split('\n').map(s => s.trim()).filter(Boolean) : [],
      zip_codes_served: zipCodesInput ? zipCodesInput.split(',').map(s => s.trim()).filter(Boolean) : [],
    }
    router.put(`/admin/users/${user.id}`, { user: submitData })
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
              <UserCog className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle>Edit User</CardTitle>
              <CardDescription>Update user information and clinician profile</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Account Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Account Information</h3>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    {...register('name')}
                  />
                  {formErrors.name && (
                    <p className="text-sm text-destructive">{formErrors.name.message}</p>
                  )}
                  {errors?.name && (
                    <p className="text-sm text-destructive">{errors.name[0]}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={user.email}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                </div>
              </div>

              <div className="flex items-center space-x-2 pt-2">
                <Checkbox
                  id="admin"
                  checked={adminValue}
                  onCheckedChange={(checked) => setValue('admin', checked === true)}
                />
                <Label htmlFor="admin" className="text-sm font-normal cursor-pointer">
                  Admin privileges
                </Label>
              </div>
            </div>

            <Separator />

            {/* Credentials & Identity Section - Collapsible */}
            <Collapsible open={credentialsOpen} onOpenChange={setCredentialsOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                    Credentials & Identity
                  </h3>
                  {credentialsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4 space-y-4">
                <div className="space-y-2">
                  <Label>Professional Credentials</Label>
                  <MultiSelect
                    options={options.credentials}
                    selected={credentialsValue}
                    onChange={(values) => setValue('credentials', values)}
                    placeholder="Select credentials..."
                    searchPlaceholder="Search credentials..."
                  />
                  {errors?.credentials && (
                    <p className="text-sm text-destructive">{errors.credentials[0]}</p>
                  )}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="specialty">Specialty</Label>
                    <Input
                      id="specialty"
                      type="text"
                      placeholder="e.g., Hepatology, Infectious Disease"
                      {...register('specialty')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="npi_number">NPI Number</Label>
                    <Input
                      id="npi_number"
                      type="text"
                      placeholder="10-digit NPI"
                      {...register('npi_number')}
                    />
                    {errors?.npi_number && (
                      <p className="text-sm text-destructive">{errors.npi_number[0]}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>License States</Label>
                  <MultiSelect
                    options={options.us_states}
                    selected={licenseStatesValue}
                    onChange={(values) => setValue('license_states', values)}
                    placeholder="Select states..."
                    searchPlaceholder="Search states..."
                    maxDisplayed={5}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state_license_numbers">State License Numbers</Label>
                  <Input
                    id="state_license_numbers"
                    type="text"
                    placeholder="Enter license numbers (comma-separated)"
                    value={licenseNumbersInput}
                    onChange={(e) => setLicenseNumbersInput(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Separate multiple license numbers with commas</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="board_certifications">Board Certifications</Label>
                  <Input
                    id="board_certifications"
                    type="text"
                    placeholder="e.g., ABIM Gastroenterology, AAHIVS"
                    value={certificationsInput}
                    onChange={(e) => setCertificationsInput(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Separate multiple certifications with commas</p>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <Separator />

            {/* Practice & Affiliation Section - Collapsible */}
            <Collapsible open={practiceOpen} onOpenChange={setPracticeOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                    Practice & Affiliation
                  </h3>
                  {practiceOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="practice_name">Practice Name</Label>
                    <Input
                      id="practice_name"
                      type="text"
                      placeholder="Enter practice name"
                      {...register('practice_name')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Practice Type</Label>
                    <Select
                      value={practiceTypeValue || ''}
                      onValueChange={(value) => setValue('practice_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select practice type" />
                      </SelectTrigger>
                      <SelectContent>
                        {options.practice_types.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors?.practice_type && (
                      <p className="text-sm text-destructive">{errors.practice_type[0]}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="affiliated_organizations">Affiliated Organizations</Label>
                  <Input
                    id="affiliated_organizations"
                    type="text"
                    placeholder="e.g., Mass General, Partners Healthcare"
                    value={organizationsInput}
                    onChange={(e) => setOrganizationsInput(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Separate multiple organizations with commas</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="practice_addresses">Practice Addresses</Label>
                  <textarea
                    id="practice_addresses"
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Enter each address on a new line"
                    value={addressesInput}
                    onChange={(e) => setAddressesInput(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Enter each address on a separate line</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zip_codes_served">ZIP Codes Served</Label>
                  <Input
                    id="zip_codes_served"
                    type="text"
                    placeholder="e.g., 02115, 02116, 02118"
                    value={zipCodesInput}
                    onChange={(e) => setZipCodesInput(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Separate multiple ZIP codes with commas</p>
                </div>

                <div className="flex flex-col gap-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="correctional_facility_access"
                      checked={correctionalAccessValue}
                      onCheckedChange={(checked) => setValue('correctional_facility_access', checked === true)}
                    />
                    <Label htmlFor="correctional_facility_access" className="text-sm font-normal cursor-pointer">
                      Has correctional facility access
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="community_health_center_affiliation"
                      checked={chcAffiliationValue}
                      onCheckedChange={(checked) => setValue('community_health_center_affiliation', checked === true)}
                    />
                    <Label htmlFor="community_health_center_affiliation" className="text-sm font-normal cursor-pointer">
                      Community Health Center (CHC) affiliation
                    </Label>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <Separator />

            {/* Care Cascade Roles Section - Collapsible */}
            <Collapsible open={rolesOpen} onOpenChange={setRolesOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                    Care Cascade Roles
                  </h3>
                  {rolesOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4 space-y-4">
                <div className="space-y-2">
                  <Label>Care Cascade Roles</Label>
                  <MultiSelect
                    options={options.care_cascade_roles}
                    selected={careCascadeRolesValue}
                    onChange={(values) => setValue('care_cascade_roles', values)}
                    placeholder="Select roles..."
                    searchPlaceholder="Search roles..."
                    maxDisplayed={4}
                  />
                  {errors?.care_cascade_roles && (
                    <p className="text-sm text-destructive">{errors.care_cascade_roles[0]}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Select the roles this clinician performs in the HCV care cascade
                  </p>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => router.visit('/admin/users')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Updating...' : 'Update User'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
